import React, { useState } from 'react';
import { Input } from '@/components/ui/input';
import { formatNumber, parseTRYInput } from '../utils/formatCurrency';

/**
 * Number input that displays with tr-TR thousand separators when not focused,
 * and shows the raw number when focused so the user can easily edit it.
 *
 *   On focus: shows raw value (e.g. "10000")
 *   On blur:  shows formatted value (e.g. "10.000,00" or "10.000" if digits=0)
 *   onChange: receives the raw numeric value (Number) so parent state stays clean.
 *
 * Accepts the same props as <Input> plus:
 *   - value: number | string  (parent's raw number)
 *   - onChange: (n: number) => void
 *   - digits: number (decimal places to show when blurred, default 2)
 *   - allowDecimal: if true, accepts comma/dot during typing
 */
const FormattedNumberInput = React.forwardRef(function FormattedNumberInput(
  { value, onChange, digits = 2, allowDecimal = true, onBlur, onFocus, ...rest },
  ref
) {
  const [focused, setFocused] = useState(false);
  const [draft, setDraft] = useState('');

  const numericValue =
    typeof value === 'number' ? value : parseTRYInput(value);
  const safeNumeric = Number.isFinite(numericValue) ? numericValue : 0;

  const displayValue = focused
    ? draft
    : safeNumeric === 0 && (value === '' || value == null)
      ? ''
      : formatNumber(safeNumeric, digits);

  const handleFocus = (e) => {
    setFocused(true);
    // Show raw value (no thousand separators) so user can edit easily.
    // Use the raw number with up to `digits` decimals stripped of trailing zeros.
    const raw =
      safeNumeric === 0
        ? ''
        : String(safeNumeric).replace('.', ',');
    setDraft(raw);
    if (onFocus) onFocus(e);
  };

  const handleBlur = (e) => {
    setFocused(false);
    const parsed = parseTRYInput(draft);
    if (Number.isFinite(parsed)) {
      if (parsed !== safeNumeric) onChange(parsed);
    } else if (draft === '') {
      if (safeNumeric !== 0) onChange(0);
    }
    if (onBlur) onBlur(e);
  };

  const handleChange = (e) => {
    let v = e.target.value;
    // Only digits, comma, dot, minus during typing
    const allowed = allowDecimal ? /[^\d.,-]/g : /[^\d-]/g;
    v = v.replace(allowed, '');
    setDraft(v);
    const parsed = parseTRYInput(v);
    if (Number.isFinite(parsed)) onChange(parsed);
    else if (v === '' || v === '-') onChange(0);
  };

  return (
    <Input
      ref={ref}
      type="text"
      inputMode="decimal"
      value={displayValue}
      onChange={handleChange}
      onFocus={handleFocus}
      onBlur={handleBlur}
      {...rest}
    />
  );
});

export default FormattedNumberInput;
